export interface PostalCodeValidationRule {
  pattern: RegExp;
  errorMessage: string;
  description?: string;
}

export const POSTAL_CODE_PATTERNS: { [countryCode: string]: PostalCodeValidationRule } = {
  FR: {
    pattern: /^\d{5}$/,
    errorMessage: 'Le code postal français doit contenir exactement 5 chiffres',
    description: 'Format français: 12345',
  },
  UK: {
    pattern: /^[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}$/i,
    errorMessage: 'Le code postal britannique doit être au format valide (ex: SW1A 1AA, M1 1AA)',
    description: 'Format britannique: SW1A 1AA ou M1 1AA',
  },
  DE: {
    pattern: /^\d{5}$/,
    errorMessage: 'Le code postal allemand doit contenir exactement 5 chiffres',
    description: 'Format allemand: 12345',
  },
  BE: {
    pattern: /^\d{4}$/,
    errorMessage: 'Le code postal belge doit contenir exactement 4 chiffres',
    description: 'Format belge: 1234',
  },
  LU: {
    pattern: /^\d{4}$/,
    errorMessage: 'Le code postal luxembourgeois doit contenir exactement 4 chiffres',
    description: 'Format luxembourgeois: 1234',
  },
  CH: {
    pattern: /^\d{4}$/,
    errorMessage: 'Le code postal suisse doit contenir exactement 4 chiffres',
    description: 'Format suisse: 1234',
  },
  IT: {
    pattern: /^\d{5}$/,
    errorMessage: 'Le code postal italien doit contenir exactement 5 chiffres',
    description: 'Format italien: 12345',
  },
  ES: {
    pattern: /^\d{5}$/,
    errorMessage: 'Le code postal espagnol doit contenir exactement 5 chiffres',
    description: 'Format espagnol: 12345',
  },
  PL: {
    pattern: /^\d{2}-?\d{3}$/,
    errorMessage: 'Le code postal polonais doit être au format XX-XXX ou XXXXX',
    description: 'Format polonais: 12-345 ou 12345',
  },
  HU: {
    pattern: /^\d{4}$/,
    errorMessage: 'Le code postal hongrois doit contenir exactement 4 chiffres',
    description: 'Format hongrois: 1234',
  },
  RO: {
    pattern: /^\d{6}$/,
    errorMessage: 'Le code postal roumain doit contenir exactement 6 chiffres',
    description: 'Format roumain: 123456',
  },
  BU: {
    pattern: /^\d{4}$/,
    errorMessage: 'Le code postal bulgare doit contenir exactement 4 chiffres',
    description: 'Format bulgare: 1234',
  },
  MA: {
    pattern: /^\d{5}$/,
    errorMessage: 'Le code postal marocain doit contenir exactement 5 chiffres',
    description: 'Format marocain: 12345',
  },
  TU: {
    pattern: /^\d{5}$/,
    errorMessage: 'Le code postal turc doit contenir exactement 5 chiffres',
    description: 'Format turc: 12345',
  },
  UA: {
    pattern: /^\d{5}$/,
    errorMessage: 'Le code postal ukrainien doit contenir exactement 5 chiffres',
    description: 'Format ukrainien: 12345',
  },
  ZA: {
    pattern: /^\d{4}$/,
    errorMessage: 'Le code postal sud-africain doit contenir exactement 4 chiffres',
    description: 'Format sud-africain: 1234',
  },
};

export class PostalCodeValidator {
  static validate(postalCode: string, countryCode: string): string {
    if (!postalCode) {
      return null;
    }

    const trimmedPostalCode = postalCode.trim();

    if (!trimmedPostalCode) {
      return null;
    }

    const rule = POSTAL_CODE_PATTERNS[countryCode];

    if (!rule) {
      if (trimmedPostalCode.length < 2) {
        return 'Le code postal doit contenir au moins 2 caractères';
      }
      return null;
    }

    if (!rule.pattern.test(trimmedPostalCode)) {
      return rule.errorMessage;
    }

    return null;
  }

  static getValidationRule(countryCode: string): PostalCodeValidationRule {
    return POSTAL_CODE_PATTERNS[countryCode] || null;
  }

  static getSupportedCountries(): string[] {
    return Object.keys(POSTAL_CODE_PATTERNS);
  }

  static hasValidationRule(countryCode: string): boolean {
    return countryCode in POSTAL_CODE_PATTERNS;
  }
}
